// Ported from the legacy rules_mixin (ADA-4437 etapp 3). Pure predicates — the field
// components build v-form rules (with translated messages) on top of these. Bank lookup,
// normalization and formatting are NOT validation and live in bankAccount.ts / format.ts
// (PR #912 review).

import Kontonummer from 'kontonummer'

import {
  bankNameForClearing,
  bankRuleForClearing,
  normalizeBankAccount,
  normalizeGiro,
  passesGiroLuhn,
  passesLuhn,
} from '@/utils/bankAccount'

/**
 * True when a field value is unset: null, undefined or ''. The empty string counts as unset
 * because it is the stored shape for a cleared field (forms seed and diff with `?? ''`) — the
 * legacy falsy checks treated it the same, without this swallowing other falsy values.
 */
export function isBlank(value: string | null | undefined): boolean {
  return value == null || value === ''
}

export function isValidEmail(value: string): boolean {
  // Deliberate deviation from the legacy pattern (PR #895 review): the old `[\w-]{2,4}` TLD
  // cap rejected real addresses (.museum, .online, …), and the flat form avoids the
  // nested-quantifier shape the security scanner flags.
  // `-` last in each class: `[\w-.]` only parses as a literal dash under Annex B (it is a
  // SyntaxError with the `u`/`v` flag), so keep the unambiguous form (PR #895 review).
  // `+` in the local part is valid and in real use (`namn+etikett@domän`) — the pattern rejected
  // those until PR #896 review (Svante).
  return /^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(value)
}

// ---------------------------------------------------------------------------
// Payout account validation (ADA-4345, tightened in ADA-4344, ADA-4350 and ADA-4349)
//
// Three layers, deliberately separate:
//
// 1. SHAPE (blocking). Mirrors the api's own regexes — RegexHelper.CLEARING_NUMBER,
//    ACCOUNT_NUMBER, BANKGIRO_NUMBER, PLUSGIRO_NUMBER. Anything failing these would be rejected
//    by the api anyway, so the form must not submit it. For giro numbers the gate is judged on
//    the NORMALIZED value, because that is what is sent and stored (ADA-4350 decision #5) and the
//    zero-strip can move a number across a length boundary.
//
// 2. SPECIFICATION (blocking). What a published specification proves impossible:
//    - Bank accounts (ADA-4344): an unknown bank, a malformed clearing number, or a TYPE 1
//      account failing its check digit — the specification guarantees verifiability there —
//      plus a Swedbank 8xxx account over ten digits (the bank's own cap, outside the registry).
//      `isValidBankAccount`/`bankAccountIssue` below.
//    - Bankgiro (ADA-4350): the check digit, on the NORMALIZED number. Bankgirot's own manual
//      (10-modul.pdf) states mod 10 on the last digit without reservation — unlike plusgiro,
//      which is hedged with "som regel" — so a bankgiro failing Luhn cannot exist.
//      `bankgiroIssue` below.
//    Plusgiro's check digit is deliberately absent from this layer — see layer 3.
//    The api enforces the same line server-side (SwedishBankAccountHelper, SwedishGiroHelper),
//    so blocking it here only moves the rejection to where the debtor can fix it.
//
// 3. PLAUSIBILITY (warning, never blocking). Strict-mode check digits via the `kontonummer`
//    package for bank accounts, and Luhn for plusgiro. What remains here is the class where the
//    specification does NOT guarantee a verifiable check digit — the TYPE 2 accounts, and
//    plusgiro. Real numbers fail it, so a warning gets the typo fixed in the common case without
//    creating a dead end in the rare one.
//    Plusgiro stays in this layer permanently (ADA-4349 decision #1, reversing the blocking line
//    that card first proposed): a block is a hard stop with no override — the api's x_rule
//    applies to staff PATCHes too — so a genuine plusgiro failing Luhn would have no registration
//    path at all, for the debtor OR for support. Its specification hedges the rule with "som
//    regel", which is exactly the class this layer exists for. What the specification states
//    without reservation — the 2–8 digit form — keeps blocking, in layer 1.
// ---------------------------------------------------------------------------

/** Shape only, matching the api's RegexHelper.CLEARING_NUMBER. */
export function isValidClearingNumber(value: string): boolean {
  return /^[0-9]{4,5}$/.test(value)
}

/** Shape only, matching the api's RegexHelper.ACCOUNT_NUMBER. */
export function isValidAccountNumber(value: string): boolean {
  return /^[0-9]{5,15}$/.test(value)
}

/** Shape only, matching the api's RegexHelper.BANKGIRO_NUMBER. */
export function isValidBankgiroNumber(value: string): boolean {
  return /^[0-9]{7,8}$/.test(value)
}

/** Shape only, matching the api's RegexHelper.PLUSGIRO_NUMBER. */
function isValidPlusgiroNumber(value: string): boolean {
  return /^[0-9]{2,8}$/.test(value)
}

/**
 * Whether a plusgiro number has a payable FORM — the shape rule applied to the NORMALIZED value.
 *
 * This is the blocking gate for plusgiro (ADA-4349): form and length are the parts of the
 * specification stated without reservation, so they block where the check digit only warns.
 * It has to be judged after normalization, because the zero-strip can take a shape-valid entry
 * below the two-digit floor ('00' → '', '007' → '7') and the api answers exactly that with
 * `invalid_plusgiro_length`.
 *
 * Empty does not pass, like `bankgiroIssue`: a cleared field is a no-op the CALLER decides on.
 */
export function isValidNormalizedPlusgiroNumber(value: string): boolean {
  return isValidPlusgiroNumber(normalizeGiro(value))
}

/**
 * Why a pair is impossible per the bank registry, or undefined when it is payable.
 *
 * This is the BLOCKING line (ADA-4344 decision #1), the client-side mirror of the api's
 * SwedishBankAccountHelper: shape, the five-digit clearing gate (only Swedbank's 8xxx-x, whose
 * fifth digit makes the clearing itself Luhn-checkable), a known bank, Swedbank's ten-digit
 * account cap for 8xxx, and — via `kontonummer` in semi mode — the TYPE 1 check digit. Semi-mode
 * CONSTRUCTION throwing is the blocking signal; its `valid` flag also turns false for TYPE 2
 * issues, which are warning-class here (layer 3), so `Kontonummer.valid()` must not be used for this.
 */
export function bankAccountIssue(
  clearingNumber: string,
  accountNumber: string
): 'unknown_bank' | 'invalid_clearing' | 'invalid_account' | 'invalid_account_length' | undefined {
  const normalized = normalizeBankAccount(clearingNumber, accountNumber)

  if (!isValidClearingNumber(normalized.clearingNumber)) return 'invalid_clearing'
  if (
    normalized.clearingNumber.length === 5 &&
    (!normalized.clearingNumber.startsWith('8') || !passesLuhn(normalized.clearingNumber))
  ) {
    // The package silently reinterprets some five-digit forms — gate them explicitly so the
    // debtor is told the clearing number is the problem, exactly like the api does.
    return 'invalid_clearing'
  }
  if (bankNameForClearing(normalized.clearingNumber) == null) return 'unknown_bank'
  if (!isValidAccountNumber(normalized.accountNumber)) return 'invalid_account'
  if (!/[1-9]/.test(normalized.accountNumber)) return 'invalid_account' // all zeros: the api blocks it for every bank type
  const rule = bankRuleForClearing(normalized.clearingNumber)
  if (rule != null && normalized.accountNumber.length > rule.accountMaxLength) {
    // The bank pays to at most this many digits; for Swedbank 8xxx the eleventh is almost always the clearing's fifth digit.
    return 'invalid_account_length'
  }
  try {
    void new Kontonummer(normalized.clearingNumber, normalized.accountNumber, { mode: 'semi' })
    return undefined
  } catch {
    return 'invalid_account'
  }
}

/**
 * Whether the registry allows paying to this pair — the blocking gate for submit/save
 * (ADA-4344). `false` means the api would reject the write with a 422; a `true` can still
 * carry a type 2 warning from `isPlausibleBankAccount`.
 */
export function isValidBankAccount(clearingNumber: string, accountNumber: string): boolean {
  return bankAccountIssue(clearingNumber, accountNumber) === undefined
}

/**
 * Why a bankgiro number is impossible per Bankgirot's specification, or undefined when it is
 * payable.
 *
 * The BLOCKING line for bankgiro (ADA-4350 decision #1), the client-side mirror of the api's
 * SwedishGiroHelper: judged on the NORMALIZED number, so the length is what remains after the
 * leading zeros go, and the mod 10 check runs on the same digits the api will store and pay to.
 * One token for both failures — length and check digit are the same "this is not a bankgiro"
 * answer to the debtor, who cannot act on the distinction.
 *
 * Empty does not pass here (like `bankAccountIssue`): a cleared field is a no-op the CALLER
 * decides on, not something this predicate can tell apart from a half-typed number.
 */
export function bankgiroIssue(value: string): 'invalid_bankgiro' | undefined {
  const normalized = normalizeGiro(value)
  if (!isValidBankgiroNumber(normalized)) return 'invalid_bankgiro'
  if (!passesGiroLuhn(normalized)) return 'invalid_bankgiro'
  return undefined
}

/**
 * Whether a clearing + account number pair passes the bank's own check digit and length rules.
 *
 * Only meaningful once both pass their shape checks. Callers must treat `false` as "warn", not
 * "reject" — see the note above.
 */
export function isPlausibleBankAccount(clearingNumber: string, accountNumber: string): boolean {
  // Judged on the NORMALIZED pair, like the blocking layer — a zero-padded but valid type 1
  // account must not carry a warning the block just cleared.
  const normalized = normalizeBankAccount(clearingNumber, accountNumber)
  if (!isValidClearingNumber(normalized.clearingNumber) || !isValidAccountNumber(normalized.accountNumber)) {
    return false
  }
  try {
    return Kontonummer.valid(normalized.clearingNumber, normalized.accountNumber)
  } catch {
    return false
  }
}

// Bankgiro has no plausibility function: its check digit is specification-guaranteed, so the
// whole of that rule blocks in `bankgiroIssue` above (ADA-4350 decision #7) and a warning class
// for it would be unreachable.

/**
 * Whether a plusgiro number's check digit adds up. Warning-level permanently (ADA-4349
 * decision #1), like the account check: plusgiro's specification hedges the rule ("som regel"),
 * so a number the check cannot confirm must not lock the debtor out of their own money — the api
 * stores it and logs the failure instead. Callers must treat `false` as "warn", never "reject";
 * the form gate is `isValidNormalizedPlusgiroNumber`.
 *
 * Judged on the NORMALIZED value, like the blocking layers — Luhn is invariant under left
 * zero-padding, but the shape gate around it must see the same digits the api will store.
 */
export function isPlausiblePlusgiroNumber(value: string): boolean {
  const normalized = normalizeGiro(value)
  return isValidPlusgiroNumber(normalized) && passesGiroLuhn(normalized)
}
